import React, { ReactNode } from 'react';
import {
  Checkbox,
  CheckboxProps,
  Flex,
  Label,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { Control, RegisterOptions, useController } from 'react-hook-form';

interface CookbookCheckboxWrapperBaseProps {
  name: string;
  control: Control;
  rules?: RegisterOptions;
  label?: string | ReactNode;
  onChange?: (value: boolean) => void;
}

export type CookbookCheckboxWrapperProps = CookbookCheckboxWrapperBaseProps &
  Omit<CheckboxProps, 'onChange'>;

export const CookbookCheckboxWrapper = ({
  name,
  control,
  rules,
  indeterminate,
  label,
  disabled,
  ...cookbookCheckboxProps
}: CookbookCheckboxWrapperProps): JSX.Element => {
  const { field } = useController({
    name,
    rules,
    control,
    defaultValue: control.defaultValuesRef.current[name] ?? false,
  });

  const onChange = (): void => {
    if (disabled) {
      return;
    }

    field.onChange(!field.value);
    cookbookCheckboxProps.onChange?.(!field.value);
  };

  /*
   * Since the cookbook checkbox wrapper doesn't accept custom labels by default, we need to make some adjustments:
   * If the label is a string, we will render the checkbox with the label as the text prop.
   * If the label is not a string, we will render the checkbox and label separately
   *
   * Platform team has in plan to fix this in: https://jira.grubhub.com/browse/FEPL-2990, so we need to get back
   * to this code and remove this workaround once the issue is resolved.
   */
  const isLabelString = typeof label === 'string';
  const checkboxProps = {
    ...field,
    ...cookbookCheckboxProps,
    onChange,
    checked: field.value,
    indeterminate: indeterminate && field.value,
    value: field.value,
  };

  return !isLabelString ? (
    <Flex>
      {/* 
        Cookbook sets "min-width: 0" when custom label is not provided, which makes the checkbox width less then it should be.
        That is why we need to set flexShrink to 0 to prevent the checkbox from shrinking.
      */}
      <Stack x="x-sm" flexShrink={0}>
        <Flex>
          <Checkbox aria-label={name} {...checkboxProps} disabled={disabled} />
        </Flex>
      </Stack>
      <Label
        // added this to display the same styling on custom label as on string label
        // we can't apply opacity on disabled checkbox with custom label as it modifies all the children (i.e. tooltips)
        className="cursorPointer"
        style={{
          userSelect: 'none',
        }}
        onClick={onChange}
      >
        {label}
      </Label>
    </Flex>
  ) : (
    <Checkbox {...checkboxProps} text={label as string} disabled={disabled} />
  );
};
